import { Box, Typography, makeStyles } from "@material-ui/core";
import { Autocomplete, AutocompleteProps } from "@material-ui/lab";
import dayjs from "dayjs";
import * as React from "react";

import { BottomSheet } from "@/components/BottomSheet";
import { CustomButton } from "@/components/Button";
import { CustomForm as LegacyForm, CustomAutoCompleteForm } from "@/components/Form";
import LegacyLabel from "@/components/FormLabel";
import { UploadComponent as LegacyUpload } from "@/components/ImageUpload";
import { CustomSelector as LegacySelector } from "@/components/Selecter";
import { FormRow } from "@/components/selection/FormRow";
import { WheelPicker } from "@/components/selection/WheelPicker";
import { DatePickerComponent as LegacyDatePicker } from "@/components/YearMonthPicker";
import theme from "@/theme";

import { RegistrationContext, registrationFieldLabels } from "./RegistrationContext";

const useStyles = makeStyles(() => ({
  info: { display: "inline-block", verticalAlign: "middle", marginRight: 8 },
  helper: { fontSize: 12, color: theme.palette.text.secondary, lineHeight: 1.5, marginTop: 8, marginBottom: 4 },
  options: { display: "flex", flexDirection: "column" },
  option: {
    minHeight: 53,
    border: 0,
    borderBottom: `1px solid ${theme.palette.semantic["border/default"]}`,
    padding: "16px 12px",
    background: "none",
    textAlign: "left",
    color: theme.palette.text.primary,
    font: "inherit",
    cursor: "pointer",
  },
  selected: { backgroundColor: theme.palette.semantic["state/selected-bg"], color: theme.palette.primary.main },
  // シートの残り高さを候補へ渡し、入力欄・見出し・決定ボタンはスクロールさせない。
  search: {
    display: "flex",
    flexDirection: "column",
    overflowY: "hidden",
    "& .MuiAutocomplete-root": { flexShrink: 0 },
    "& .MuiFormControl-root": { minWidth: 0, flexShrink: 0 },
    "& .MuiAutocomplete-popper": {
      position: "static",
      width: "100% !important",
      display: "flex",
      flexDirection: "column",
      minHeight: 0,
    },
    "& .MuiAutocomplete-paper": {
      display: "flex",
      flexDirection: "column",
      minHeight: 0,
      overflow: "hidden",
    },
    "& .MuiAutocomplete-listbox": { minHeight: 0, overscrollBehavior: "contain" },
  },
}));

// 明示必須や条件付き必須は各Controllerのpropsを優先する。
// legacyの required は見た目用だったため、名前のある必須項目はschemaに合わせる。
const optionalFields = new Set([
  "layout",
  "block",
  "property_image",
  "asking_price",
  "other_monthly_income",
  "misc_cost",
  "management_company_commission",
  "shared_utility_expenses",
  "misc_building_cost",
  "type_of_the_income_of_the_beginning_of_contract",
  "loan_interest_rate_percentage",
]);
const isOptional = (name: string | undefined, required?: boolean) =>
  required ? false : name ? optionalFields.has(name) : !required;

type FormProps = React.ComponentProps<typeof LegacyForm> & { name?: string; optional?: boolean };
export const CustomForm = React.forwardRef<HTMLInputElement, FormProps>((props, ref) => {
  const v2 = React.useContext(RegistrationContext);
  const { optional, ...inputProps } = props;
  const classes = useStyles();
  const [open, setOpen] = React.useState(false);
  const [draft, setDraft] = React.useState("");
  const label = props.label || registrationFieldLabels[props.name] || props.labelLight || "入力";
  if (!v2) return <LegacyForm {...inputProps} ref={ref} />;
  const start = () => {
    setDraft(String(props.value ?? ""));
    setOpen(true);
  };
  const close = () => {
    setOpen(false);
    props.onBlur?.({ target: { value: props.value, name: props.name } } as React.FocusEvent<HTMLInputElement>);
  };
  return (
    <Box data-registration-field={props.name || label}>
      <FormRow
        label={label}
        value={
          props.value !== "" && props.value != null
            ? `${
                props.formatNumeric && Number.isFinite(Number(String(props.value).replace(/,/g, "")))
                  ? Number(String(props.value).replace(/,/g, "")).toLocaleString("ja-JP", { maximumFractionDigits: 10 })
                  : props.value
              }${props.unitSuffix || ""}`
            : undefined
        }
        placeholder="未入力"
        error={props.error ? String(props.errorText || "入力内容を確認してください") : undefined}
        errorSurface
        disabled={props.disabled}
        optional={optional ?? isOptional(props.name, props.required)}
        onClick={start}
      />
      {props.helperText && <Typography className={classes.helper}>{props.helperText}</Typography>}
      <BottomSheet
        open={open}
        title={label}
        description={props.info}
        onOpen={start}
        onClose={close}
        maxHeightPercent={70}
        footer={
          <CustomButton
            type="button"
            buttonSize="L"
            onClick={() => {
              props.onChange({
                type: "change",
                target: { value: draft, name: props.name },
              } as React.ChangeEvent<HTMLInputElement>);
              close();
            }}
          >
            決定
          </CustomButton>
        }
      >
        <LegacyForm
          {...inputProps}
          ref={ref}
          label={undefined}
          labelLight={undefined}
          helperText={undefined}
          required={false}
          info={undefined}
          value={draft}
          onChange={(event) => setDraft(event.target.value)}
          onBlur={undefined}
        />
      </BottomSheet>
    </Box>
  );
});
CustomForm.displayName = "RegistrationForm";

type SelectorProps = React.ComponentProps<typeof LegacySelector> & { name?: string; optional?: boolean };
export const CustomSelector = React.forwardRef<HTMLSelectElement, SelectorProps>((props, ref) => {
  const v2 = React.useContext(RegistrationContext);
  const { optional, ...inputProps } = props;
  const classes = useStyles();
  const [open, setOpen] = React.useState(false);
  if (!v2) return <LegacySelector {...inputProps} ref={ref} />;
  const label = props.label || registrationFieldLabels[props.name] || "選択";
  const value = props.options.find((option) => option.value === props.value)?.label || props.value;
  const close = () => {
    setOpen(false);
    props.onBlur?.({
      target: { value: props.value, name: props.name },
    } as unknown as React.FocusEvent<HTMLInputElement>);
  };
  return (
    <Box data-registration-field={props.name || label}>
      <FormRow
        label={label}
        value={value !== "" && value != null ? `${value}${props.suffix || ""}` : undefined}
        error={props.error ? props.errorText : undefined}
        errorSurface
        disabled={props.disabled}
        optional={optional ?? isOptional(props.name, props.required)}
        onClick={() => setOpen(true)}
      />
      <BottomSheet open={open} title={label} onOpen={() => setOpen(true)} onClose={close} maxHeightPercent={70}>
        <Box className={classes.options} role="listbox" aria-label={label}>
          {props.options.map((option) => (
            <button
              type="button"
              key={option.value}
              role="option"
              aria-selected={option.value === props.value}
              className={`${classes.option} ${option.value === props.value ? classes.selected : ""}`}
              onClick={() => {
                if (option.value !== props.value)
                  props.onChange({
                    type: "change",
                    target: { value: option.value, name: props.name },
                  } as unknown as React.ChangeEvent<{
                    value: unknown;
                  }>);
                close();
              }}
            >
              {option.label}
            </button>
          ))}
        </Box>
      </BottomSheet>
    </Box>
  );
});
CustomSelector.displayName = "RegistrationSelector";

type DateProps = React.ComponentProps<typeof LegacyDatePicker> & {
  name?: string;
  optional?: boolean;
  onBlur?: () => void;
};
export const DatePickerComponent = React.forwardRef<HTMLInputElement, DateProps>((props, ref) => {
  const v2 = React.useContext(RegistrationContext);
  const { optional, ...inputProps } = props;
  const [open, setOpen] = React.useState(false);
  const [year, setYear] = React.useState(dayjs().year());
  const [month, setMonth] = React.useState(dayjs().month() + 1);
  if (!v2) return <LegacyDatePicker {...inputProps} ref={ref} />;
  const label = props.label || registrationFieldLabels[props.name] || "年月";
  const date = props.value ? dayjs(props.value) : null;
  const close = () => {
    setOpen(false);
    props.onBlur?.();
  };
  const start = () => {
    setYear(date?.isValid() ? date.year() : dayjs().year());
    setMonth(date?.isValid() ? date.month() + 1 : dayjs().month() + 1);
    setOpen(true);
  };
  return (
    <Box data-registration-field={props.name || label}>
      <FormRow
        label={label}
        value={date?.isValid() ? date.format("YYYY年M月") : undefined}
        error={props.error ? props.errorText : undefined}
        errorSurface
        optional={optional ?? isOptional(props.name, props.required)}
        onClick={start}
      />
      <BottomSheet
        open={open}
        title={label}
        onOpen={start}
        onClose={close}
        maxHeightPercent={70}
        footer={
          <CustomButton
            type="button"
            buttonSize="L"
            onClick={() => {
              props.onChange(
                dayjs()
                  .date(1)
                  .year(year)
                  .month(month - 1)
                  .startOf("day")
                  .toDate()
              );
              close();
            }}
          >
            決定
          </CustomButton>
        }
      >
        <WheelPicker
          columns={[
            {
              label: "年",
              values: Array.from({ length: 201 }, (_, i) => 1900 + i),
              value: year,
              initialValue: year,
              unit: "年",
              onChange: setYear,
            },
            {
              label: "月",
              values: Array.from({ length: 12 }, (_, i) => i + 1),
              value: month,
              initialValue: month,
              unit: "月",
              onChange: setMonth,
            },
          ]}
          echo={`${year}年${month}月`}
        />
      </BottomSheet>
    </Box>
  );
});
DatePickerComponent.displayName = "RegistrationDatePicker";

export const RegistrationAutocomplete = <
  T,
  Multiple extends boolean | undefined = false,
  DisableClearable extends boolean | undefined = false,
  FreeSolo extends boolean | undefined = false
>({
  registrationLabel,
  registrationError,
  registrationRequired,
  registrationOnBlur,
  ...props
}: AutocompleteProps<T, Multiple, DisableClearable, FreeSolo> & {
  registrationLabel: string;
  registrationError?: string;
  registrationRequired?: boolean;
  registrationOnBlur?: () => void;
}) => {
  const v2 = React.useContext(RegistrationContext);
  const classes = useStyles();
  const [open, setOpen] = React.useState(false);
  const initialText = React.useRef("");
  const inputEvent = React.useRef<Parameters<NonNullable<typeof props.onInputChange>>[0]>(null);
  if (!v2) return <Autocomplete {...props} />;
  const value =
    props.value == null
      ? undefined
      : typeof props.value === "string"
      ? props.value
      : props.getOptionLabel?.(props.value as T);
  const start = () => {
    initialText.current = props.inputValue ?? value ?? "";
    setOpen(true);
  };
  const close = (commit = true) => {
    if (!commit && props.freeSolo && inputEvent.current)
      props.onInputChange?.(inputEvent.current, initialText.current, "input");
    inputEvent.current = null;
    setOpen(false);
    registrationOnBlur?.();
  };
  // 元のAutocompleteをそのまま使用し、検索・自動入力・freeSoloのイベントを保持する。
  return (
    <Box data-registration-field={registrationLabel}>
      <FormRow
        label={registrationLabel}
        value={value}
        placeholder={props.freeSolo ? "未入力" : "未選択"}
        error={registrationError}
        errorSurface
        optional={!registrationRequired}
        disabled={props.disabled}
        onClick={start}
      />
      <BottomSheet
        open={open}
        title={registrationLabel}
        onOpen={start}
        onClose={() => close(false)}
        maxHeightPercent={70}
        contentClassName={classes.search}
        footer={
          props.freeSolo ? (
            <CustomButton type="button" buttonSize="L" onClick={() => close()}>
              決定
            </CustomButton>
          ) : undefined
        }
      >
        <Autocomplete
          {...props}
          disablePortal
          openOnFocus
          onInputChange={(event, text, reason) => {
            inputEvent.current = event;
            props.onInputChange?.(event, text, reason);
          }}
          onChange={(event, selected, reason, details) => {
            props.onChange?.(event, selected, reason, details);
            if (!props.freeSolo || reason === "select-option") close();
          }}
        />
      </BottomSheet>
    </Box>
  );
};

export const CustomFormLabel: React.FC<React.ComponentProps<typeof LegacyLabel>> = (props) => {
  const classes = useStyles();
  const v2 = React.useContext(RegistrationContext);
  return v2 ? (
    props.info || props.helperText ? (
      <Typography className={classes.helper}>
        <img src="/assets/image/ds_v2_registration_info.svg" alt="" className={classes.info} />
        {[props.helperText, props.info].filter(Boolean).join(" ")}
      </Typography>
    ) : null
  ) : (
    <LegacyLabel {...props} />
  );
};

export const RegistrationAutoCompleteForm = React.forwardRef<
  HTMLInputElement,
  React.ComponentProps<typeof CustomAutoCompleteForm>
>((props, ref) => {
  const v2 = React.useContext(RegistrationContext);
  return <CustomAutoCompleteForm {...props} required={v2 ? false : props.required} ref={ref} />;
});
RegistrationAutoCompleteForm.displayName = "RegistrationAutoCompleteForm";

// ファイルのサイズ検証・同じファイルの再選択・onChangeは共通部品に委譲する。
export const RegistrationUpload: React.FC<React.ComponentProps<typeof LegacyUpload>> = (props) => {
  const v2 = React.useContext(RegistrationContext);
  return (
    <Box data-registration-upload={v2 || undefined}>
      <LegacyUpload
        {...props}
        buttonLabel={v2 ? "写真を追加" : props.buttonLabel}
        description={v2 ? "未設定でも登録できます（あとから追加できます）" : props.description}
      />
    </Box>
  );
};
